import type { InboxConversationItemProps } from '../../CentralInbox.types';

import { Skeleton, Typography } from '@recruit-robin/robin-theme/web-components';
import { ConversationNotificationPill } from 'components/ui/molecules/ConversationNotificationPill/ConversationNotificationPill';
import { IconSizeEnum } from 'components/ui/molecules/HistoryTab/HistoryCard/HistoryCard.types';
import { useStyles } from './InboxConversationItem.styles';

import { formatDate } from 'components/ui/molecules/HistoryTab/formatDate';
import useVariant from 'components/ui/molecules/HistoryTab/HistoryCard/HistoryCard.useVariant';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useContext } from 'react';
import { CandidateHistoryAction } from 'services/candidates/types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCandidateStore } from 'stores/candidate/candidate.store';

export const InboxConversationItem = ({ conversation }: InboxConversationItemProps) => {
  const pageContext = usePageContext();
  const selectedCandidate = useCandidateStore((state) => state.selectedCandidate);
  const { locale } = useContext(LocalizationContext);
  const isActive =
    pageContext === PageContext.CentralInbox ? selectedCandidate?.es_person_id === conversation.esPersonId : false;
  const classes = useStyles({ isActive });
  const { icon } = useVariant({
    action: conversation.lastMessageFromRecruiter
      ? CandidateHistoryAction.Contacted
      : CandidateHistoryAction.MessageReceived,
    source: conversation.conversationSource,
    atsName: undefined,
    iconSize: IconSizeEnum.SMALL,
  });

  const isLoading = conversation.lastMessageDate === null;
  const lastMessageDate = formatDate(conversation.lastMessageDate ?? new Date(), locale, true, true);

  return (
    <div css={classes.root}>
      <div css={classes.title}>
        <Typography
          ellipsis
          variant={(v) => (conversation.unreadCount > 0 ? v.heading[5] : v.body.long)}
          color={(v) => v.neutral.variant[80]}>
          {conversation.candidateName}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" font={(f) => f.supporting['outfit']} width={90} />
        ) : (
          <Typography
            css={classes.noBreak}
            variant={(v) => v.supporting['outfit']}
            color={(v) => v.neutral.variant[60]}>
            {lastMessageDate}
          </Typography>
        )}
      </div>
      <div css={classes.subtitle}>
        {isLoading ? (
          <div css={classes.noMargin}>
            <Skeleton variant="text" font={(f) => f.supporting['label-tiny']} width={130} />
          </div>
        ) : (
          <div css={classes.flexAlign}>
            <div css={classes.icon}>{icon}</div>
            <Typography
              ellipsis
              variant={(v) => v.supporting['label-tiny']}
              color={(v) => (conversation.unreadCount > 0 ? v.neutral.variant[70] : v.neutral.variant[50])}>
              {conversation.user.firstName} / {conversation.campaign.name}
            </Typography>
          </div>
        )}
        <ConversationNotificationPill unreadCount={conversation.unreadCount} />
      </div>
    </div>
  );
};
